import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Sparkles, ShieldCheck } from "lucide-react";
import ImageUploader from "../components/ImageUploader";
import VinScanner from "../components/VinScanner";
import VehicleInspectionCapture, { inspectionPhotosComplete, inspectionPhotosPayload } from "../components/VehicleInspectionCapture";
import InspectionQrHandoff from "../components/InspectionQrHandoff";
import KycModal from "../components/KycModal";
import { formatBookingError } from "../lib/bookingErrors";
import { AI_NAME, AI_POSSESSIVE } from "../lib/brand";
import { formatMoney, priceSuggestionCurrency } from "../lib/currencyDisplay";
import { VEHICLE_BODY_TYPES } from "../lib/vehicleTypes";

export default function NewVehicle() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [kycOpen, setKycOpen] = useState(false);
  const [form, setForm] = useState({
    make: "", model: "", trim: "", year: 2023, vehicle_type: "sedan", transmission: "automatic",
    powertrain: "gasoline",
    seats: 5, location_city: "", location_address: "", daily_rate: 199,
    exterior_color: "", interior_color: "",
    allows_one_way: false, one_way_fee: 30, one_way_fee_per_km: "", one_way_fee_max: "", included_km_per_day: 320, overage_fee_per_km: 0.16,
    allows_return_date_extension: false, allows_return_address_change: false,
    delivery_lead_days: "",
    operating_open: "", operating_close: "", after_hours_fee: "",
    vin: "", registration_number: "",
    description: "", features: "",
    images: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [conditionPhotos, setConditionPhotos] = useState({});
  const [qrToken, setQrToken] = useState(null);

  const kycOk = user?.kyc_status === "approved";

  useEffect(() => {
    if (user && user.kyc_status !== "approved") setKycOpen(true);
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (user?.kyc_status !== "approved") {
      setKycOpen(true);
      toast.error("Verify your identity before listing a vehicle");
      return;
    }
    if (!inspectionPhotosComplete(conditionPhotos)) {
      toast.error("Complete the AI condition scan (all 6 angles) before submitting");
      return;
    }
    const baselineImages = form.images.length > 0
      ? form.images
      : (() => {
          const front = conditionPhotos.front || conditionPhotos.driver_side || Object.values(conditionPhotos)[0];
          if (!front?.file_id) return [];
          return [`/api/files/${front.file_id}`];
        })();
    if (baselineImages.length === 0) {
      toast.error("Upload at least one photo or complete the condition scan");
      return;
    }
    const vin = (form.vin || "").trim().toUpperCase();
    if (!/^[A-HJ-NPR-Z0-9]{11,17}$/.test(vin)) {
      toast.error("Enter a valid VIN (11–17 characters, letters and numbers only)");
      return;
    }
    const reg = (form.registration_number || "").trim().toUpperCase();
    if (!reg || reg.length < 2) {
      toast.error("Enter your car number plate");
      return;
    }
    if (!(form.location_address || "").trim()) {
      toast.error("Enter the full street address where the car is kept");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        vin,
        registration_number: reg,
        images: baselineImages,
        year: parseInt(form.year),
        seats: parseInt(form.seats),
        daily_rate: parseFloat(form.daily_rate),
        one_way_fee: parseFloat(form.one_way_fee),
        one_way_fee_per_km: form.one_way_fee_per_km !== "" && form.one_way_fee_per_km != null ? parseFloat(form.one_way_fee_per_km) : null,
        one_way_fee_max: form.one_way_fee_max !== "" && form.one_way_fee_max != null ? parseFloat(form.one_way_fee_max) : null,
        included_km_per_day: parseInt(form.included_km_per_day, 10),
        overage_fee_per_km: parseFloat(form.overage_fee_per_km),
        delivery_lead_days: form.delivery_lead_days ? parseInt(form.delivery_lead_days, 10) : null,
        operating_open: form.operating_open || null,
        operating_close: form.operating_close || null,
        after_hours_fee: form.after_hours_fee !== "" && form.after_hours_fee != null ? parseFloat(form.after_hours_fee) : null,
        features: form.features.split(",").map(s => s.trim()).filter(Boolean),
      };
      const created = await axios.post(`${API}/vehicles`, payload, { withCredentials: true });
      await axios.post(`${API}/inspections`, {
        phase: "listing_baseline",
        vehicle_id: created.data.vehicle_id,
        photos: inspectionPhotosPayload(conditionPhotos),
      }, { withCredentials: true });
      toast.success("Listing submitted with AI condition baseline");
      navigate("/host?tab=cars");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 403 && d === "kyc_required") {
        setKycOpen(true);
        toast.error("Verify your identity before listing");
      } else if (d === "vin_already_listed") toast.error("This VIN is already listed on Z-Cars");
      else if (d === "location_address_required") toast.error(formatBookingError(d));
      else if (Array.isArray(d)) toast.error(d[0]?.msg || "Invalid vehicle details");
      else toast.error(formatBookingError(d) || "Failed");
    } finally { setSubmitting(false); }
  };

  const geocodeAddress = async (address) => {
    const raw = (address || "").trim();
    if (!raw) return;
    try {
      const r = await axios.get(`${API}/geocode`, { params: { address: raw }, withCredentials: true });
      if (r.data?.location_city) {
        setForm((f) => ({ ...f, location_city: r.data.location_city }));
      }
    } catch {
      /* best-effort */
    }
  };

  const resolveCity = () => {
    if ((form.location_city || "").trim()) return form.location_city.trim();
    const parts = (form.location_address || "").split(",").map((s) => s.trim()).filter(Boolean);
    if (parts.length >= 3) return parts[parts.length - 2];
    if (parts.length >= 2) return parts[parts.length - 1];
    if (parts.length === 1) return parts[0];
    return "";
  };
  const locationForCurrency = {
    city: [form.location_city, form.location_address].filter(Boolean).join(" "),
  };
  const localPriceCurrency = priceSuggestionCurrency(null, locationForCurrency);
  const suggestionCurrency = priceSuggestionCurrency(suggestion, locationForCurrency);
  const money = (amount) => formatMoney(amount, suggestionCurrency);

  const aiSuggest = async () => {
    const year = parseInt(form.year, 10);
    const city = resolveCity();
    if (!form.make?.trim() || !form.model?.trim() || !city) {
      toast.error("Fill make, model, and street address first"); return;
    }
    if (!Number.isFinite(year) || year < 1980 || year > new Date().getFullYear() + 1) {
      toast.error("Enter a valid year before requesting a price suggestion"); return;
    }
    setSuggesting(true);
    try {
      const r = await axios.post(`${API}/pricing/suggest`, {
        kind: "vehicle", make: form.make.trim(), model: form.model.trim(), trim: form.trim?.trim() || undefined,
        year, vehicle_type: form.vehicle_type, transmission: form.transmission,
        city,
      }, { withCredentials: true });
      setSuggestion(r.data);
      setForm(f => ({ ...f, daily_rate: r.data.suggested_rate }));
      toast.success(`Suggested ${formatMoney(r.data.suggested_rate, r.data.currency)}/day (${r.data.source || "estimate"})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not get suggestion");
    } finally { setSuggesting(false); }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-12" data-testid="new-vehicle-page">
      <div className="eyebrow mb-2">Host</div>
      <h1 className="zc-headline text-4xl md:text-5xl">List a vehicle</h1>
      <p className="text-[var(--zc-text-2)] mt-3">Tell us about your machine. Approval is typically within a day.</p>

      {!kycOk && (
        <div
          className="mt-6 p-4 rounded-lg border border-[var(--zc-border)] flex flex-wrap items-center justify-between gap-3"
          style={{ background: "rgba(245,158,11,0.08)" }}
          data-testid="nv-kyc-banner"
        >
          <div className="flex items-start gap-3 text-sm text-[var(--zc-text-2)]">
            <ShieldCheck size={18} className="text-[var(--zc-warn,#f59e0b)] shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-[var(--zc-text)]">Identity verification required</div>
              <div className="mt-1">Hosts must complete KYC before adding cars to the fleet — same as driveway listings.</div>
            </div>
          </div>
          <button type="button" onClick={() => setKycOpen(true)} className="px-4 h-10 zc-btn-primary text-sm shrink-0" data-testid="nv-kyc-start">
            Verify identity
          </button>
        </div>
      )}

      <fieldset disabled={!kycOk} className={"mt-10 border-0 p-0 m-0 min-w-0 " + (!kycOk ? "opacity-60" : "")}>
      <form onSubmit={submit} className="space-y-6" data-testid="new-vehicle-form">
        <div className="zc-card p-5 md:p-6" data-testid="nv-registration-section">
          <div className="eyebrow mb-1">Number plate &amp; VIN</div>
          <p className="text-xs text-[var(--zc-text-3)] mb-4">
            Both are required to host on Z-Cars. Renters do not see these on the listing — only you and our team use them for verification and insurance.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <Field
              label="Number plate"
              testid="nv-registration"
              v={form.registration_number}
              on={(v) => setForm((f) => ({ ...f, registration_number: v.toUpperCase() }))}
              placeholder="e.g. AB12 CDE"
              hint="License / registration plate on the vehicle"
              mono
              required
              maxLength={20}
            />
            <Field
              label="VIN"
              testid="nv-vin"
              v={form.vin}
              on={(v) => setForm((f) => ({ ...f, vin: v.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/gi, "").slice(0, 17) }))}
              placeholder="17 characters on the dashboard sticker"
              hint="Vehicle Identification Number"
              mono
              required
              maxLength={17}
            />
          </div>
          <VinScanner
            testid="nv-vin-scanner"
            onConfirm={(vin) => setForm((f) => ({ ...f, vin }))}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Make" testid="nv-make" v={form.make} on={v => setForm(f => ({ ...f, make: v }))} required />
          <Field label="Model" testid="nv-model" v={form.model} on={v => setForm(f => ({ ...f, model: v }))} required />
          <Field label="Trim" testid="nv-trim" v={form.trim} on={v => setForm(f => ({ ...f, trim: v }))} placeholder="e.g. M Sport, Plaid" />
          <Field label="Year" type="number" testid="nv-year" v={form.year} on={v => setForm(f => ({ ...f, year: v }))} />
          <Field label={`Daily rate (${localPriceCurrency})`} type="number" testid="nv-rate" v={form.daily_rate} on={v => setForm(f => ({ ...f, daily_rate: v }))} />
          <SelectField label="Body type" testid="nv-type" v={form.vehicle_type} on={v => setForm(f => ({ ...f, vehicle_type: v }))} options={VEHICLE_BODY_TYPES} />
          <SelectField label="Powertrain" testid="nv-powertrain" v={form.powertrain} on={v => setForm(f => ({ ...f, powertrain: v }))} options={[
            ["gasoline","Gasoline"],["electric","Electric"],["phev","Plug-in hybrid"],["hybrid","Hybrid"]
          ]} />
          <SelectField label="Transmission" testid="nv-trans" v={form.transmission} on={v => setForm(f => ({ ...f, transmission: v }))} options={[["automatic","Automatic"],["manual","Manual"]]} />
          <Field label="Exterior color" testid="nv-ext-color" v={form.exterior_color} on={v => setForm(f => ({ ...f, exterior_color: v }))} placeholder="e.g. Midnight Black" />
          <Field label="Interior color" testid="nv-int-color" v={form.interior_color} on={v => setForm(f => ({ ...f, interior_color: v }))} placeholder="e.g. Tan leather" />
          <Field label="Seats" type="number" testid="nv-seats" v={form.seats} on={v => setForm(f => ({ ...f, seats: v }))} />
          <Field
            label="Full address"
            testid="nv-address"
            v={form.location_address}
            on={(v) => setForm((f) => ({ ...f, location_address: v }))}
            onBlur={() => geocodeAddress(form.location_address)}
            required
            placeholder="123 Main St, Unit 4, Toronto, ON"
            hint="Full address where renters pick up the car"
          />
          <Field
            label="City"
            testid="nv-city"
            v={form.location_city}
            on={(v) => setForm((f) => ({ ...f, location_city: v }))}
            placeholder="Auto-filled from address"
            hint="Auto-filled from address"
            readOnly={!!form.location_city && !!(form.location_address || "").trim()}
          />
          <Field label="Delivery lead (days, optional)" type="number" testid="nv-delivery" v={form.delivery_lead_days} on={v => setForm(f => ({ ...f, delivery_lead_days: v }))} placeholder="e.g. 1" hint="How many days before the car can be ready for pickup" />
        </div>

        <div className="zc-card p-5 md:p-6">
          <div className="eyebrow mb-1">Trip policies</div>
          <p className="text-xs text-[var(--zc-text-3)] mb-4">Choose which changes renters may request after booking. Both amendment options are off by default.</p>
          <div className="space-y-6">
            <div>
              <div className="text-xs font-semibold text-[var(--zc-text-2)] mb-3">Changes after booking</div>
              <div className="space-y-3">
                <PolicyCheckbox
                  checked={form.allows_return_date_extension}
                  onChange={(checked) => setForm((f) => ({ ...f, allows_return_date_extension: checked }))}
                  testid="nv-return-extension"
                  label="Allow renters to request a later return date"
                  hint="The current return can never be shortened."
                />
                <PolicyCheckbox
                  checked={form.allows_return_address_change}
                  onChange={(checked) => setForm((f) => ({ ...f, allows_return_address_change: checked }))}
                  testid="nv-return-address-change"
                  label="Allow renters to request a different return address"
                  hint={form.allows_one_way ? "Different addresses must still pass the one-way distance check." : "Enable one-way trips below for returns to a different address."}
                />
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-[var(--zc-text-2)] mb-3">One-way trips</div>
              <div className="grid md:grid-cols-2 gap-4">
                <label className="flex items-center gap-2 text-sm md:col-span-2">
                  <input type="checkbox" checked={form.allows_one_way} onChange={e => setForm(f => ({ ...f, allows_one_way: e.target.checked }))} className="w-4 h-4 accent-[var(--zc-accent)]" data-testid="nv-one-way" />
                  Allow different return location (one-way trips)
                </label>
                <Field label={`Base one-way fee (${localPriceCurrency})`} type="number" testid="nv-one-way-fee" v={form.one_way_fee} on={v => setForm(f => ({ ...f, one_way_fee: v }))} hint="Minimum fee before distance" />
                <Field label={`${localPriceCurrency}/km one-way surcharge`} type="number" testid="nv-one-way-km" v={form.one_way_fee_per_km} on={v => setForm(f => ({ ...f, one_way_fee_per_km: v }))} placeholder="0.75" />
                <Field label={`Max one-way fee (${localPriceCurrency})`} type="number" testid="nv-one-way-max" v={form.one_way_fee_max} on={v => setForm(f => ({ ...f, one_way_fee_max: v }))} placeholder="250" />
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-[var(--zc-text-2)] mb-3">Distance allowance</div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Included km per day" type="number" testid="nv-km-day" v={form.included_km_per_day} on={v => setForm(f => ({ ...f, included_km_per_day: v }))} hint="Included daily mileage before overage charges" />
                <Field label={`${localPriceCurrency}/km overage`} type="number" testid="nv-overage" v={form.overage_fee_per_km} on={v => setForm(f => ({ ...f, overage_fee_per_km: v }))} hint="Charged per km above the daily allowance" />
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-[var(--zc-text-2)] mb-3">Operating hours</div>
              <p className="text-xs text-[var(--zc-text-3)] mb-3">Set when your vehicle is available for pick-up and return. Outside these hours an after-hours fee applies. Leave blank to use the default (10 pm – 6 am window, $25 fee).</p>
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="Open (HH:MM)" testid="nv-op-open" v={form.operating_open} on={v => setForm(f => ({ ...f, operating_open: v }))} placeholder="08:00" hint="e.g. 08:00" />
                <Field label="Close (HH:MM)" testid="nv-op-close" v={form.operating_close} on={v => setForm(f => ({ ...f, operating_close: v }))} placeholder="18:00" hint="e.g. 18:00" />
                <Field label={`After-hours fee (${localPriceCurrency})`} type="number" testid="nv-ah-fee" v={form.after_hours_fee} on={v => setForm(f => ({ ...f, after_hours_fee: v }))} placeholder="25" hint="Fee per after-hours slot" />
              </div>
              {form.operating_open && form.operating_close && (
                <p className="text-xs text-[var(--zc-text-3)] mt-3">
                  Available {form.operating_open} – {form.operating_close} daily.
                  {form.after_hours_fee ? ` After-hours fee: ${localPriceCurrency === "CAD" ? "CA$" : "$"}${form.after_hours_fee}` : " After-hours fee: $25 (default)"}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="-mt-2">
          <button type="button" onClick={aiSuggest} disabled={suggesting} className="inline-flex items-center gap-2 px-4 h-10 zc-btn-outline-secondary text-xs" data-testid="nv-ai-suggest">
            <Sparkles size={12} /> {suggesting ? `Asking ${AI_NAME}…` : "AI suggest a fair price"}
          </button>
          {suggestion && (
            <div className="mt-3 p-5 rounded-lg zc-card" style={{ background: "linear-gradient(180deg, var(--zc-bg-2) 0%, var(--zc-secondary-soft) 100%)" }} data-testid="nv-ai-out">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-[var(--zc-accent)]" />
                <div className="eyebrow">{AI_POSSESSIVE} price insight</div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div>
                  <div className="eyebrow">Suggested rate</div>
                  <div className="zc-price text-2xl mt-0.5">{money(suggestion.suggested_rate)}<span className="zc-unit normal-case tracking-normal">/day</span></div>
                </div>
                <div>
                  <div className="eyebrow">Typical range</div>
                  <div className="zc-meta mt-2 font-mono">{money(suggestion.low)} – {money(suggestion.high)}/day</div>
                </div>
              </div>
              {suggestion.rationale && (
                <p className="zc-hint text-[var(--zc-text-2)] mt-3 leading-relaxed" data-testid="price-insight-rationale">{suggestion.rationale}</p>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="text-xs text-[var(--zc-text-3)]">Description</label>
          <textarea className="zc-input w-full p-3 text-sm h-28 mt-1" value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="nv-desc"
            placeholder="Tell renters what makes this vehicle special..." />
        </div>

        <div>
          <label className="text-xs text-[var(--zc-text-3)]">Features (comma separated)</label>
          <input className="zc-input w-full h-11 px-3 text-sm mt-1" value={form.features}
            onChange={e => setForm(f => ({ ...f, features: e.target.value }))} data-testid="nv-features"
            placeholder="Sport seats, Carbon fiber, Premium audio" />
        </div>

        <div>
          <label className="text-xs text-[var(--zc-text-3)] block mb-2">Photos</label>
          <p className="zc-meta mb-2">Optional if your condition scan includes a clear front angle — we&apos;ll use it as the listing cover.</p>
          <ImageUploader value={form.images} onChange={(imgs) => setForm(f => ({ ...f, images: imgs }))} max={4} testid="nv-uploader" />
        </div>

        <div className="zc-card p-5 md:p-6" data-testid="nv-condition-section">
          <div className="eyebrow mb-1">AI condition baseline</div>
          <p className="text-xs text-[var(--zc-text-3)] mb-4">
            Capture guided photos of your car. Z-Cars AI scans for dents, scratches, and wear — this becomes the reference for every future pickup and return.
            If your camera won&apos;t start, you can upload a photo for each angle instead.
          </p>
          <InspectionQrHandoff
            phase="listing_baseline"
            active
            photos={conditionPhotos}
            onPhotosSync={(incoming) => {
              if (!incoming || !Object.keys(incoming).length) return;
              setConditionPhotos(prev => {
                const next = { ...prev };
                for (const [angle, entry] of Object.entries(incoming)) {
                  if (entry?.file_id) next[angle] = entry;
                }
                return next;
              });
            }}
            onToken={setQrToken}
            testid="nv-condition-qr"
          />
          <VehicleInspectionCapture
            value={conditionPhotos}
            onChange={setConditionPhotos}
            qrToken={qrToken}
            inspectionPhase="listing_baseline"
            testid="nv-condition-capture"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-[var(--zc-border)]">
          <button type="button" onClick={() => navigate(-1)} className="px-4 h-11 zc-btn-ghost text-sm" data-testid="nv-cancel">Cancel</button>
          <button type="submit" disabled={submitting} className="px-5 h-11 zc-btn-primary text-sm flex items-center gap-2" data-testid="nv-submit">
            <Plus size={14} /> {submitting ? "Submitting…" : "Submit listing"}
          </button>
        </div>
      </form>
      </fieldset>
      <KycModal
        open={kycOpen}
        onClose={() => setKycOpen(false)}
        onApproved={async () => {
          setKycOpen(false);
          await checkAuth?.();
          toast.success("Identity verified — you can submit your listing");
        }}
      />
    </div>
  );
}

function Field({ label, v, on, type = "text", required, testid, placeholder, hint, mono, maxLength, readOnly, onBlur }) {
  return (
    <div>
      <label className="text-xs text-[var(--zc-text-3)]">{label}</label>
      <input
        type={type}
        required={required}
        readOnly={readOnly}
        placeholder={placeholder}
        maxLength={maxLength}
        className={"zc-input w-full h-11 px-3 text-sm mt-1 " + (mono ? "font-mono uppercase tracking-wide" : "") + (readOnly ? " bg-[var(--zc-bg-2)] text-[var(--zc-text-2)]" : "")}
        value={v}
        onChange={(e) => on(e.target.value)}
        onBlur={onBlur}
        data-testid={testid}
      />
      {hint && <div className="zc-meta mt-1">{hint}</div>}
    </div>
  );
}
function SelectField({ label, v, on, options, testid }) {
  return (
    <div>
      <label className="text-xs text-[var(--zc-text-3)]">{label}</label>
      <select className="zc-input w-full h-11 px-3 text-sm mt-1" value={v} onChange={e => on(e.target.value)} data-testid={testid}>
        {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
      </select>
    </div>
  );
}

function PolicyCheckbox({ checked, onChange, testid, label, hint }) {
  return (
    <label className="flex items-start gap-3 text-sm">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 mt-0.5 accent-[var(--zc-accent)]" data-testid={testid} />
      <span>
        <span className="block text-[var(--zc-text)]">{label}</span>
        <span className="zc-meta block mt-0.5">{hint}</span>
      </span>
    </label>
  );
}
